import { ReactNode } from 'react';
import { GlobalProps } from '@bloomberg/media-ui-core/types';
import type { Placement } from '@floating-ui/react';
import { CredentialResponse, PromptMomentNotification } from 'google-one-tap';
import { labelIds } from 'src/i18n';

export enum REGIONS {
  US = 'AMER',
  EUROPE = 'EUR',
  ASIA = 'APAC',
  UK = 'UK',
  MIDDLE_EAST = 'MIDEAST',
  AFRICA = 'AFRICA',
  JAPAN = 'JAPAN',
  CANADA = 'CANADA',
  LATAM = 'LATAM',
}

export enum LANGUAGES {
  EN = 'en',
}

export enum THEMES {
  DARK = 'dark',
  LIGHT = 'light',
  BLUE = 'blue',
}

export enum AuthorSessionStatus {
  'LIVE' = 'LIVE',
  'STARTING SOON' = 'STARTING SOON',
}

export type SubscriptionData = {
  product?: string;
  type?: string;
  offerType?: string;
  entitlements?: string[];
  bulkSubscriptionId?: string | number;
  bulkSubscriptionName?: string;
  features?: string[];
  corporateHubAccess?: boolean;
};

export type User = {
  firstName?: string;
  lastName?: string;
  isLoggedIn?: boolean;
  isSubscriber?: boolean;
  showUpgrade?: boolean;
  subscriptionsData?: SubscriptionData[];
};

export enum TIPKEY {
  CORPORATE_HUB = 'corporateHub',
}

export type Tip = {
  key: string;
  eyebrow: string;
  text: string;
  icon: ReactNode;
  placement: Placement[];
  onDismiss: () => void;
};

export type NavTips = Record<TIPKEY, Tip>;

export type DropdownListItem = {
  id: string;
  text: string;
  url: string;
  label?: string | null;
};

export type SubmenuItem = {
  dropdownListItems: DropdownListItem[];
  id: string;
  text: string;
  label?: string;
  url?: string;
};

export type MenuItem = {
  id: string;
  text: string;
} & (
  | {
      useSubmenu?: false;
      submenuItems: Record<string, SubmenuItem>;
      dropdownListItems?: DropdownListItem[];
    }
  | {
      useSubmenu: true;
      submenuItems: Record<string, SubmenuItem>;
    }
);

export type MarketDataSubmenuItems = {
  market_data_links: SubmenuItem;
  news_links: SubmenuItem;
  market_data_tickers: SubmenuItem;
};

export type MenuItems = {
  menuItemsDesktop: Record<string, MenuItem>;
  menuItemsMobile: Record<string, MenuItem>;
};

export type MenuDesktopProps = GlobalProps & {
  onMenuItemActive?: () => void;
  marketDataTickersUrl?: string;
  menuItems: Record<string, MenuItem>;
  theme?: THEMES;
  region: REGIONS;
  useSubmenu?: boolean;
  desktopBreakpointClassName?: string;
  trackingFunction: (eventLabel: string) => void;
  locale?: string;
};

export type Suggestion = {
  text: string;
  url: string;
};

export type Company = {
  url: string;
  name: string;
  ticker_symbol: string;
  score: number;
};

export type Author = {
  name: string;
  score: number;
  slug: string;
  primary_site: string;
};

export type Person = {
  name: string;
  score: number;
  url: string;
  organization?: string;
  ticker_symbol?: string;
  primary_site?: string;
};

export type SearchResult = {
  text: string;
  url: string;
  id: string;
};

export type SearchResults = {
  suggestions: {
    id: string;
    text: string;
    dropdownListItems: SearchResult[];
  };
  people: {
    id: string;
    text: string;
    dropdownListItems: SearchResult[];
  };
  securities: {
    id: string;
    text: string;
    dropdownListItems: SearchResult[];
  };
};

export type RegionPickerDropdownOption = {
  value: string;
  text: string;
  switchEditionPrompt?: string;
  url: string;
};

export type LabelId = keyof typeof labelIds;

export type UserDropdownOption = {
  text: string;
  url: string;
  badge?: string;
};

export type UserDropdownOptionWithTranslationConfig = {
  defaultText: string;
  textId: LabelId;
  url: string;
  badge?: string;
};

export type UserOptionMenuItem = {
  text: string;
  items: UserDropdownOption[];
};

export type UserOptionMenuItemWithTranslationConfig = {
  defaultText: string;
  textId?: LabelId;
  items: UserDropdownOptionWithTranslationConfig[];
};

export type UserOptionMenuHeader = 'interests' | 'settings' | 'help';

export type UserOptionsMenuItems = {
  [k in UserOptionMenuHeader]: UserOptionMenuItem;
};

export type UserOptionsMenuItemsWithTranslationConfig = {
  [k in UserOptionMenuHeader]: UserOptionMenuItemWithTranslationConfig;
};

export type BannerDataAuthorSession = {
  imageUrl?: string;
  status: AuthorSessionStatus;
  title: string;
  url: string;
};

export type BannerDataBreakingNews = {
  headline: string;
  identifier: string;
  url?: string;
};

export type BannerDataBreakingNewsLiveStream = {
  imageUrl?: string;
  region: string;
  streamId: string;
  theme?: 'QT' | 'BTV';
  title: string;
  url: string;
  VideoPlayerComponent?: React.ReactNode;
};

export type BannerData = {
  regionalAuthorSession?: BannerDataAuthorSession;
  globalAuthorSession?: BannerDataAuthorSession;
  regionalBreakingNews?: BannerDataBreakingNews;
  globalBreakingNews?: BannerDataBreakingNews;
  regionalBreakingNewsLiveStream?: BannerDataBreakingNewsLiveStream;
  globalBreakingNewsLiveStream?: BannerDataBreakingNewsLiveStream;
};

export type BannerCloseReason = 'EscapeKey' | 'ExitClick' | 'AutoHideTimer';

declare global {
  interface Window {
    googleOneTapDataCallback: (googleResponse: CredentialResponse) => void;
    momentCallback: (notification: PromptMomentNotification) => void;
  }
}
